/** Action items, "Notifications": the notification inbox - ADR-0092 phase 1 (in-app only) and
 * ADR-0212 decision 6c (a tab of Action items).
 *
 * **Every word on a row was served.** The sentence is the API's `text`, built at read time from ids
 * the caller can see; this screen shows it, the time it was created and whether it has been read,
 * and nothing it composed itself - no record title, no figure, no kind-to-sentence table. The Type
 * column and its filter are the *family* of the kind (the part before the dot: approval, task,
 * period, filing, source), a grouping and not a sentence; a kind is also carried as a `data-`
 * attribute for tests and is never rendered whole, **and is not counted either**: a tile saying "3
 * approvals" would be a sentence the screen composed from a kind.
 *
 * **Three states, each its own rendering**: a failed read is the shared `ReadFailed` (never an
 * empty inbox - an inbox that says "nothing for you" because a request failed is the defect this
 * product has recorded thirteen times), an empty inbox is one sentence, and a populated one is a
 * grid. There is no mark-all-read because the API has no such route.
 *
 * **Reading is two acts, and neither is implied by the other.** Following a notification's link
 * marks it read (that is "opening" it); a button marks it read without leaving, for the one that
 * has no link and for a person who wants it off the count. A refused write leaves the row unread
 * and says so.
 */

import { BellSimple } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { NOTIFICATION_PAGE, useMarkNotificationRead, useNotifications } from "../api/notifications";
import type { NotificationItem } from "../api/notifications";
import { useReader } from "../api/profile";
import { ReadFailed, WriteFailed } from "../components/Loaded";
import { EmptyState, FilterLine, GridTable, StatusChip, TablePage, Tile, TileStrip } from "../components/ui";
import { EMPTY_FILTERS } from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import { instantDate, instantStamp } from "../lib/format";
import { relativeWhen } from "../lib/relativeWhen";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import { useGridSort } from "./gridSort";
import { keepSearch } from "./search";
import { useScreen } from "./useScreen";

/** The family of a kind: the part before the dot. A grouping word, never a sentence. */
const FAMILY: Record<string, string> = {
  approval: "Approval",
  task: "Task",
  period: "Period",
  filing: "Filing",
  source: "Data gap",
};

function familyOf(kind: string): string | undefined {
  return FAMILY[kind.split(".")[0] ?? ""];
}

/** When it arrived, relative inside a week and a date after it; the exact stamp (date, time and
 * zone) is the hover text, so the short form never replaces the exact one. */
function Stamp({ at, now }: { at: string; now: number }) {
  const reader = useReader();
  return (
    <time dateTime={at} title={instantStamp(at, reader)}>
      {relativeWhen(at, now, reader)}
    </time>
  );
}

/** "Now", read once and refreshed each minute, so a list left open does not go on saying "3 min
 * ago" for an hour. Held in state because a render must not read the clock itself. */
function useNow(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  return now;
}

/** Its own component so the reader's preferences are read only when there is a date to place. */
function NewestTile({ at, read }: { at: string | undefined; read: { isPending: boolean; error: unknown } }) {
  return at ? <NewestDated at={at} /> : <Tile value="-" label="Newest arrived" read={read} />;
}

function NewestDated({ at }: { at: string }) {
  const reader = useReader();
  return <Tile value={instantDate(at, reader)} label="Newest arrived" />;
}

function NotificationsBody() {
  const screen = useScreen();
  const inbox = useNotifications();
  const mark = useMarkNotificationRead();
  const now = useNow();
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);

  const items = inbox.data?.items ?? [];
  const families = [...new Set(items.map((item) => familyOf(item.kind)).filter((f): f is string => f !== undefined))];
  const picked = filter.values.type ?? [];
  const needle = filter.search.trim().toLowerCase();
  const rows = items.filter((item) => {
    if (picked.length > 0 && !picked.includes(familyOf(item.kind) ?? "")) return false;
    const state = filter.values.state ?? [];
    if (state.includes("unread") && item.read_at !== null) return false;
    return !needle || item.text.toLowerCase().includes(needle);
  });
  const { sorted, sort, setSort } = useGridSort(rows, {
    text: (r) => r.text,
    when: (r) => r.created_at,
  });
  //: The newest by timestamp, not by position: the screen does not lean on the server's order.
  const newest = [...items].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];

  const columns: GridColumn<NotificationItem>[] = [
    {
      id: "text",
      header: "Notification",
      sortable: true,
      cell: (item) => {
        const unread = item.read_at === null;
        const text = item.link ? (
          <Link
            to={item.link}
            search={keepSearch}
            title={item.text}
            onClick={() => {
              if (unread) mark.mutate(item.id);
            }}
          >
            {item.text}
          </Link>
        ) : (
          <span title={item.text}>{item.text}</span>
        );
        return (
          <span data-testid="notification" data-read={unread ? "false" : "true"} data-kind={item.kind}>
            <BellSimple size={14} weight={unread ? "fill" : "regular"} aria-hidden="true" /> {text}
          </span>
        );
      },
    },
    {
      id: "type",
      header: "Type",
      cell: (item) => familyOf(item.kind) ?? "",
    },
    {
      id: "state",
      header: "State",
      cell: (item) =>
        item.read_at === null ? (
          <StatusChip tone="info" size="sm">
            Unread
          </StatusChip>
        ) : (
          <StatusChip tone="neutral" size="sm">
            Read
          </StatusChip>
        ),
    },
    {
      id: "when",
      header: "When",
      sortable: true,
      cell: (item) => <Stamp at={item.created_at} now={now} />,
    },
    {
      id: "mark",
      header: "Mark read",
      cell: (item) =>
        item.read_at === null ? (
          <button
            type="button"
            disabled={mark.isPending && mark.variables === item.id}
            aria-label={`Mark as read: ${item.text}`}
            onClick={() => mark.mutate(item.id)}
          >
            Mark as read
          </button>
        ) : null,
    },
  ];

  const data = inbox.data;
  return (
    <ScreenFrame screen={screen} title="Notifications">
      <TablePage
        icon={BellSimple}
        title="Action items"
        label="Notifications"
        tiles={
          <TileStrip label="Notifications at a glance">
            <Tile
              value={data ? String(data.unread) : "-"}
              label="Not yet read"
              infoKey="actions.notifications"
              read={inbox}
              testId="notifications-unread"
            />
            <Tile
              value={data ? `${String(items.length)}${items.length >= NOTIFICATION_PAGE ? "+" : ""}` : "-"}
              label="Listed"
              read={inbox}
            />
            <NewestTile at={newest?.created_at} read={inbox} />
          </TileStrip>
        }
        filters={
          data && items.length === 0 ? undefined : (
            <FilterLine
              filters={[
                { id: "type", label: "Type", options: families.map((value) => ({ value, label: value })) },
                {
                  id: "state",
                  label: "State",
                  mode: "single",
                  allLabel: "All",
                  options: [{ value: "unread", label: "Unread only" }],
                },
              ]}
              state={filter}
              onChange={setFilter}
              resultCount={data ? { shown: rows.length, total: items.length, noun: "notifications" } : undefined}
            />
          )
        }
        table={
          inbox.error ? undefined : (
            <>
              <WriteFailed
                error={mark.error}
                of="that notification"
                onRetry={mark.variables ? () => mark.mutate(mark.variables) : undefined}
              />
              <GridTable
                caption="Notifications, newest first"
                columns={columns}
                rows={sorted}
                rowKey={(item) => item.id}
                loading={inbox.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={
                  <EmptyState
                    icon={BellSimple}
                    title={
                      items.length === 0 ? "No notifications." : "Nothing unread in the notifications listed."
                    }
                    compact
                    testId="notifications-empty"
                  />
                }
                testId="notifications"
              />
            </>
          )
        }
        state={
          inbox.error ? (
            <ReadFailed of="Your notifications" error={inbox.error} onRetry={() => void inbox.refetch()} />
          ) : undefined
        }
      />
    </ScreenFrame>
  );
}

export function NotificationsScreen() {
  return (
    <RequireSession>
      <NotificationsBody />
    </RequireSession>
  );
}
